import type { IBimRelationshipEdge, IBimRelationshipNode } from '@skystone/shared';

/**
 * Deterministic layered layout for the BIM relationship graph.
 *
 * Tables are grouped into vertical lanes by role (fact / other / dim), with
 * `dim_date` pulled out into its own right-hand side lane. Within each lane the
 * rows are ordered with a barycentric heuristic so connected tables sit at a
 * similar height, which keeps the orthogonal edge router from drawing long
 * crossing lines. The result is expressed in plain SVG/pixel coordinates so the
 * HTML card layer and the SVG edge layer can share the same geometry.
 */

export interface ICardLayout {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  lane: number;
}

export interface IGraphLayout {
  cards: Map<string, ICardLayout>;
  width: number;
  height: number;
}

export const CARD_WIDTH = 210;
export const HEADER_HEIGHT = 36;
export const FIELD_ROW_HEIGHT = 20;
export const CARD_VERTICAL_PADDING = 12;
export const LANE_GAP = 150;
export const ROW_GAP = 28;
export const MARGIN = 32;

const SIDE_LANE = 3;
const FACT_LANE = 0;
const OTHER_LANE = 1;
const DIM_LANE = 2;
const BARYCENTER_PASSES = 4;

export function laneForNode(node: IBimRelationshipNode): number {
  if (node.tableName === 'dim_date') {
    return SIDE_LANE;
  }

  if (node.role === 'fact') {
    return FACT_LANE;
  }

  if (node.role === 'other') {
    return OTHER_LANE;
  }

  return DIM_LANE;
}

export function cardHeight(node: IBimRelationshipNode): number {
  const fieldCount = Math.max(node.keyColumns.length, 1);
  return HEADER_HEIGHT + fieldCount * FIELD_ROW_HEIGHT + CARD_VERTICAL_PADDING;
}

function buildAdjacency(
  edges: IBimRelationshipEdge[],
  nodes: IBimRelationshipNode[],
): Map<string, Set<string>> {
  // Edges reference tables by table name; lanes are keyed by node id. Resolve
  // names to ids so barycentric ordering still works when the two differ.
  const idByTableName = new Map(nodes.map((node) => [node.tableName, node.id] as const));
  const resolve = (tableName: string): string => idByTableName.get(tableName) ?? tableName;

  const adjacency = new Map<string, Set<string>>();
  const link = (a: string, b: string): void => {
    const set = adjacency.get(a) ?? new Set<string>();
    set.add(b);
    adjacency.set(a, set);
  };

  for (const edge of edges) {
    link(resolve(edge.fromTable), resolve(edge.toTable));
    link(resolve(edge.toTable), resolve(edge.fromTable));
  }

  return adjacency;
}

function groupIntoLanes(nodes: IBimRelationshipNode[]): Map<number, IBimRelationshipNode[]> {
  const lanes = new Map<number, IBimRelationshipNode[]>();
  const sorted = [...nodes].sort((a, b) => a.tableName.localeCompare(b.tableName));

  for (const node of sorted) {
    const lane = laneForNode(node);
    const bucket = lanes.get(lane) ?? [];
    bucket.push(node);
    lanes.set(lane, bucket);
  }

  return lanes;
}

function buildOrderIndex(lanes: Map<number, IBimRelationshipNode[]>): Map<string, number> {
  const index = new Map<string, number>();
  for (const bucket of lanes.values()) {
    bucket.forEach((node, position) => index.set(node.id, position));
  }
  return index;
}

function reorderLanesOnce(
  lanes: Map<number, IBimRelationshipNode[]>,
  adjacency: Map<string, Set<string>>,
): void {
  const orderIndex = buildOrderIndex(lanes);

  for (const [lane, bucket] of lanes) {
    const scored = bucket.map((node, currentIndex) => {
      const neighbors = [...(adjacency.get(node.id) ?? [])];
      const neighborPositions = neighbors
        .map((neighbor) => orderIndex.get(neighbor))
        .filter((value): value is number => value !== undefined);

      const barycenter = neighborPositions.length > 0
        ? neighborPositions.reduce((sum, value) => sum + value, 0) / neighborPositions.length
        : currentIndex;

      return { node, barycenter };
    });

    scored.sort((a, b) => a.barycenter - b.barycenter || a.node.tableName.localeCompare(b.node.tableName));
    lanes.set(lane, scored.map((entry) => entry.node));
  }
}

function stackHeight(bucket: IBimRelationshipNode[]): number {
  const total = bucket.reduce((sum, node) => sum + cardHeight(node), 0);
  return total + Math.max(0, bucket.length - 1) * ROW_GAP;
}

export function layoutGraph(nodes: IBimRelationshipNode[], edges: IBimRelationshipEdge[]): IGraphLayout {
  const lanes = groupIntoLanes(nodes);
  const adjacency = buildAdjacency(edges, nodes);

  for (let pass = 0; pass < BARYCENTER_PASSES; pass += 1) {
    reorderLanesOnce(lanes, adjacency);
  }

  const sortedLanes = [...lanes.entries()].sort((a, b) => a[0] - b[0]);
  const tallestStack = Math.max(0, ...sortedLanes.map(([, bucket]) => stackHeight(bucket)));

  const cards = new Map<string, ICardLayout>();
  sortedLanes.forEach(([lane, bucket], laneSequence) => {
    const x = MARGIN + laneSequence * (CARD_WIDTH + LANE_GAP);
    let y = MARGIN + (tallestStack - stackHeight(bucket)) / 2;

    for (const node of bucket) {
      const height = cardHeight(node);
      cards.set(node.id, { id: node.id, x, y, width: CARD_WIDTH, height, lane });
      y += height + ROW_GAP;
    }
  });

  const width = MARGIN * 2 + sortedLanes.length * CARD_WIDTH + Math.max(0, sortedLanes.length - 1) * LANE_GAP;
  const height = MARGIN * 2 + tallestStack;

  return { cards, width, height };
}
